import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@parrot-software/design-system';
import type { PaymentHistoryViewDto } from '@subscriptions/application/dtos';
import { ExpandablePaymentRow } from '@subscriptions/presentation/components/base';
import { useIntersectionObserver } from '@subscriptions/presentation/hooks';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';
import { PaymentHistoryTablePaginationSkeleton } from './PaymentHistoryTablePaginationSkeleton';

interface PaymentHistoryTableProps {
  blockUuid?: string;
  payments: PaymentHistoryViewDto[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  showConcept?: boolean;
  showSubscription?: boolean;
}

/**
 * PaymentHistoryTable - Displays payment history records in a table format
 *
 * Columns: Fecha, Suscripción, Concepto, Status, Método de pago, Total and Factura, the last one
 * holding either the invoice download or a retry link. Empty cells display "-" as default value.
 * Método de pago is dropped below `lg`.
 *
 * The Suscripción cell carries the subscription name on a grouped charge and the store name on a
 * per-store invoice. `showSubscription` and `showConcept` drop their columns.
 *
 * `blockUuid` feeds the retry path only; the organization-level table falls back to the payment's
 * own `blockUuid`.
 *
 * When pagination props are provided, the table renders:
 * - An IntersectionObserver-attached sentinel row that triggers `onLoadMore` on scroll.
 * - Inline skeleton rows at the bottom while the next page is being fetched.
 * - A sticky "Cargar más" button inside the horizontal-scroll container so it stays
 *   reachable on narrow viewports where the sentinel may not become visible.
 */
export const PaymentHistoryTable = ({
  blockUuid,
  payments,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  showConcept = true,
  showSubscription = true,
}: PaymentHistoryTableProps) => {
  const { t } = useTranslation('paymentHistory');
  const areActionsLocked = useAreSubscriptionActionsLocked();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (uuid: string) => {
    setExpandedRows((previous) => {
      const next = new Set(previous);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  const canLoadMore = hasNextPage && !isFetchingNextPage && !!onLoadMore;

  const handleLoadMore = () => {
    if (canLoadMore) {
      onLoadMore();
    }
  };

  const observerRef = useIntersectionObserver({
    onIntersect: handleLoadMore,
    enabled: canLoadMore,
    threshold: 1.0,
  });

  const totalColumns = 5 + Number(showSubscription) + Number(showConcept);

  return (
    <div className="relative w-full min-w-0 overflow-x-auto rounded-lg border">
      <Table wrapperClassName="overflow-visible">
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead className="pl-1 pr-2.5">
              <div className="flex items-center gap-1">
                <span aria-hidden className="h-6 w-6 shrink-0" />
                {t('columns.paymentDate')}
              </div>
            </TableHead>
            {showSubscription && <TableHead className="px-2.5">{t('columns.subscription')}</TableHead>}
            {showConcept && <TableHead className="px-2.5">{t('columns.concept')}</TableHead>}
            <TableHead className="px-2.5">{t('columns.status')}</TableHead>
            <TableHead className="hidden px-2.5 lg:table-cell">{t('columns.paymentMethod')}</TableHead>
            <TableHead className="px-2.5 text-right">{t('columns.subscriptionAmount')}</TableHead>
            <TableHead className="w-[176px] px-2.5 text-right">{t('columns.invoice')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((payment) => {
            const children = payment.relatedInvoices ?? [];
            const isExpandable = children.length > 0;
            const isExpanded = expandedRows.has(payment.uuid);

            return (
              <Fragment key={payment.uuid}>
                <ExpandablePaymentRow
                  blockUuid={blockUuid}
                  isExpanded={isExpanded}
                  isRetryDisabled={areActionsLocked}
                  onToggle={isExpandable ? () => toggleRow(payment.uuid) : undefined}
                  payment={payment}
                  showConcept={showConcept}
                  showSubscription={showSubscription}
                />
                {children.map((child, index) => (
                  <ExpandablePaymentRow
                    blockUuid={blockUuid}
                    isChild
                    isExpanded={isExpanded}
                    isLastChild={index === children.length - 1}
                    isRetryDisabled={areActionsLocked}
                    key={child.uuid}
                    payment={child}
                    showConcept={showConcept}
                    showSubscription={showSubscription}
                  />
                ))}
              </Fragment>
            );
          })}

          {hasNextPage && !isFetchingNextPage && (
            <TableRow>
              <TableCell className="h-1 p-0" colSpan={totalColumns}>
                <div className="h-1" data-testid="payment-history-infinite-scroll-trigger" ref={observerRef} />
              </TableCell>
            </TableRow>
          )}

          {isFetchingNextPage && (
            <PaymentHistoryTablePaginationSkeleton showConcept={showConcept} showSubscription={showSubscription} />
          )}
        </TableBody>
      </Table>

      {canLoadMore && (
        <div className="relative w-full py-2">
          <Button
            className="sticky left-1/2 -translate-x-1/2 shadow-md"
            data-testid="payment-history-load-more-button"
            onClick={handleLoadMore}
            size="sm"
            variant="outline"
          >
            {t('buttons.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
};
